import { useCallback, useEffect, useRef, useState } from "react";
import { fetchFishingIntelligenceOutlook } from "./fishingIntelligenceApi";
import { isCoordinateFishingLocation } from "../fishingToday/fishingTodayLocation";
import { isSupportedFishingIntelligenceSpecies } from "./speciesCatalog";
import { normalizeFishingSetting } from "./fishingSetting";

const idleState = { data: null, error: null, isLoading: false };

export function useFishingIntelligence(
  speciesId,
  activeLocation,
  outlookLocationKey = "",
  declaredHabitat = null,
  sessionEvidence = null,
  fishingPlatform = null
) {
  const [state, setState] = useState({ ...idleState, requestKey: null });
  const [retryVersion, setRetryVersion] = useState(0);
  const requestIdRef = useRef(0);
  const source = activeLocation?.source;
  const latitude = activeLocation?.latitude;
  const longitude = activeLocation?.longitude;
  const supported = isSupportedFishingIntelligenceSpecies(speciesId);
  const normalizedSetting = normalizeFishingSetting(declaredHabitat);
  const habitatFeatureKey = normalizedSetting.habitatFeatures.join("|");
  const accessContext = normalizedSetting.accessContext;
  const sessionCarpPresent = sessionEvidence?.carpPresent === true;
  const sessionEvidenceSupplied = sessionEvidence !== null;
  const sessionTemperature = Number.isFinite(
    sessionEvidence?.measuredFreshwaterTemperatureC
  )
    ? sessionEvidence.measuredFreshwaterTemperatureC
    : null;
  const requestKey = supported
    ? JSON.stringify([
        speciesId,
        source,
        latitude,
        longitude,
        outlookLocationKey,
        habitatFeatureKey,
        accessContext,
        sessionEvidenceSupplied,
        sessionCarpPresent,
        sessionTemperature,
        fishingPlatform,
        retryVersion,
      ])
    : null;

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    if (!supported) {
      setState({ ...idleState, requestKey: null });
      return undefined;
    }

    const controller = new AbortController();
    const location =
      isCoordinateFishingLocation({ source, latitude, longitude })
        ? { source, latitude, longitude }
        : { source: "profile_city" };
    setState({ data: null, error: null, isLoading: true, requestKey });

    Promise.resolve()
      .then(() => {
        if (controller.signal.aborted) return null;
        return fetchFishingIntelligenceOutlook({
          speciesId,
          fishingPlatform,
          location,
          declaredHabitat: {
            habitatFeatures: habitatFeatureKey ? habitatFeatureKey.split("|") : [],
            accessContext,
          },
          sessionEvidence:
            sessionEvidenceSupplied === false
              ? null
              : {
                  carpPresent: sessionCarpPresent,
                  measuredFreshwaterTemperatureC: sessionTemperature,
                },
          signal: controller.signal,
        });
      })
      .then((data) => {
        if (data && requestId === requestIdRef.current) {
          setState({ data, error: null, isLoading: false, requestKey });
        }
      })
      .catch((error) => {
        if (error?.name !== "AbortError" && requestId === requestIdRef.current) {
          setState({ data: null, error, isLoading: false, requestKey });
        }
      });

    return () => controller.abort();
  }, [
    speciesId,
    source,
    latitude,
    longitude,
    outlookLocationKey,
    habitatFeatureKey,
    accessContext,
    sessionEvidenceSupplied,
    sessionCarpPresent,
    sessionTemperature,
    fishingPlatform,
    retryVersion,
    requestKey,
    supported,
  ]);

  const retry = useCallback(() => setRetryVersion((version) => version + 1), []);
  if (!requestKey) return { ...idleState, retry };
  if (state.requestKey !== requestKey) {
    return { data: null, error: null, isLoading: true, retry };
  }
  return { data: state.data, error: state.error, isLoading: state.isLoading, retry };
}
